/** Shared HTTP transport with explicit public and member request boundaries. */
import { supabase } from "@/lib/supabase/supabase";
import { tenantSlugFromHostname } from "@/lib/tenant";
import { ApiError, httpFetch, publicApi } from "@/lib/api/publicClient";

export { ApiError, publicApi };

async function memberFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const tenantSlug = tenantSlugFromHostname();
  if (!tenantSlug) {
    throw new ApiError(404, "Tenant unavailable");
  }

  const {
    data: { session },
  } = await supabase.auth.getSession();

  const headers = new Headers(init.headers);
  headers.set("X-Tenant", tenantSlug);
  if (session?.access_token) {
    headers.set("Authorization", `Bearer ${session.access_token}`);
  }
  return httpFetch<T>(path, { ...init, headers });
}

export type MemberRequestOptions = Pick<RequestInit, "headers" | "signal" | "cache">;

function makeApi(fetcher: <T>(path: string, init?: RequestInit) => Promise<T>) {
  return {
    get: <T>(path: string, options: MemberRequestOptions = {}) => fetcher<T>(path, options),
    post: <T>(path: string, body?: unknown, options: MemberRequestOptions = {}) =>
      fetcher<T>(path, { ...options, method: "POST", body: body === undefined ? undefined : JSON.stringify(body) }),
    patch: <T>(path: string, body?: unknown, options: MemberRequestOptions = {}) =>
      fetcher<T>(path, { ...options, method: "PATCH", body: body === undefined ? undefined : JSON.stringify(body) }),
    put: <T>(path: string, body?: unknown, options: MemberRequestOptions = {}) =>
      fetcher<T>(path, { ...options, method: "PUT", body: body === undefined ? undefined : JSON.stringify(body) }),
    delete: <T>(path: string, body?: unknown, options: MemberRequestOptions = {}) =>
      fetcher<T>(path, { ...options, method: "DELETE", body: body === undefined ? undefined : JSON.stringify(body) }),
  };
}

/** Authenticated endpoints that require both the session token and tenant claim. */
export const memberApi = makeApi(memberFetch);

/** Existing authenticated feature clients use this compatibility name. */
export const api = memberApi;
